import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import type { DotLottie } from '@lottiefiles/dotlottie-web';
import { OnboardingData } from '../../types/onboarding';
import { generatePlan } from '../../services/onboardingPlan';
import OnboardingTrack from './OnboardingTrack';
import OnboardingCadence from './OnboardingCadence';
import OnboardingFocus from './OnboardingFocus';
import OnboardingExamDate from './OnboardingExamDate';
import HonestAssessment from './HonestAssessment';
import AccountCreation from './AccountCreation';
import { X, ChevronLeft } from 'lucide-react';
import { savePreAuthWizardData, savePreAuthStudyPlan } from '../../services/preAuthWizardCache';

const PreAuthWizard: React.FC = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);

    // Lifted state for Step 4 (Focus) sub-steps
    const [subStep, setSubStep] = useState<'osp' | 'zsv'>('osp');

    // Initialize with default data
    const [data, setData] = useState<Partial<OnboardingData>>({
        planVersion: 1,
        lastUpdated: new Date().toISOString(),
        completedAt: new Date().toISOString(),
    });

    // Transition state management
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
    const [contentOpacity, setContentOpacity] = useState('opacity-100');
    const [showAccountCreation, setShowAccountCreation] = useState(false);
    const [fadeToLogin, setFadeToLogin] = useState(false);
    const pendingDataRef = useRef<Partial<OnboardingData> | null>(null);
    const [dotLottie, setDotLottie] = useState<DotLottie | null>(null);

    // Calculate total steps based on track selection
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const getTotalSteps = () => {
        const baseSteps = 6; // Track, Assessment, Time, Exam, Success, Account
        if (data.track === 'BOTH') return baseSteps + 1; // Extra step for both OSP and ZSV
        if (data.track === 'OSP' || data.track === 'ZSV') return baseSteps;
        return baseSteps - 1; // For UNSURE, might skip focus
    };

    const getCurrentStepDisplay = () => {
        // For BOTH track: 5 steps (Track, Assessment, Cadence, Focus OSP, Focus ZSV, Exam Date)
        // For OSP/ZSV: 5 steps (Track, Assessment, Cadence, Focus, Exam Date)
        if (step <= 2) return step;

        if (data.track === 'BOTH') {
            if (step === 3) return 3; // Cadence
            if (step === 4 && subStep === 'osp') return 4; // Focus OSP
            if (step === 4 && subStep === 'zsv') return 4; // Focus OSP (transitioning)
            if (step === 5) return 4; // Focus ZSV
            if (step === 6) return 5; // Exam Date
        } else {
            // OSP, ZSV, or UNSURE
            if (step === 3) return 3; // Cadence
            if (step === 4) return 4; // Focus
            if (step === 5) return 5; // Exam Date
        }
        return step;
    };

    // Callback when Lottie animation completes
    const handleAnimationComplete = useCallback(async () => {
        if (!pendingDataRef.current) return;

        const newData = pendingDataRef.current;
        pendingDataRef.current = null;

        // Remove undefined values from newData before merging
        const cleanedNewData = Object.fromEntries(
            Object.entries(newData).filter(([_, v]) => v !== undefined)
        );
        const updatedData = { ...data, ...cleanedNewData, completedAt: new Date().toISOString() };
        setData(updatedData);

        // Save progress to localStorage cache
        savePreAuthWizardData(cleanedNewData);

        if (step === 6 || (step === 5 && data.track !== 'BOTH')) {
            // Step 6 (BOTH track exam date) OR Step 5 (OSP/ZSV/UNSURE exam date)
            // -> Success Animation -> Fade -> Account Creation
            // Process the data first
            if (newData) {
                await finishWizard(newData);
            }
            // Then fade to login
            setFadeToLogin(true);
            setTimeout(() => {
                setShowSuccessAnimation(false);
                setShowAccountCreation(true);
                setFadeToLogin(false);
                setIsTransitioning(false);
                setContentOpacity('opacity-100');
            }, 500); // 500ms fade out duration
        } else {
            // Hide success animation for other steps
            setShowSuccessAnimation(false);
            setStep(step + 1);
            // Fade in new content
            setTimeout(() => {
                setContentOpacity('opacity-100');
                setIsTransitioning(false);
            }, 50);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, step]);

    // Listen for Lottie animation complete event
    useEffect(() => {
        if (!dotLottie) return;

        const handleComplete = () => {
            handleAnimationComplete();
        };

        dotLottie.addEventListener('complete', handleComplete);

        return () => {
            dotLottie.removeEventListener('complete', handleComplete);
        };
    }, [dotLottie, handleAnimationComplete]);

    const finishWizard = async (finalData: Partial<OnboardingData>) => {
        const cleanedNewData = Object.fromEntries(
            Object.entries(finalData).filter(([_, v]) => v !== undefined)
        );
        const updatedData = { ...data, ...cleanedNewData, completedAt: new Date().toISOString() };
        setData(updatedData);

        // Save final data to cache
        savePreAuthWizardData(cleanedNewData);

        // Generate the study plan
        const isPro = false; // TODO: Replace with actual pro status check
        const { plan: finalPlan, requiresUpgrade } = generatePlan(updatedData as OnboardingData, isPro);

        // Save plan to cache
        savePreAuthStudyPlan(finalPlan);

        // If upgrade required, store it
        if (requiresUpgrade) {
            localStorage.setItem('pendingUpgradePrompt', JSON.stringify({
                topic: requiresUpgrade.topic,
                contentType: requiresUpgrade.contentType,
                fallbackTopic: requiresUpgrade.fallbackTopic,
                shown: false
            }));
        }

        // Clear daily completion flag
        const today = new Date().toISOString().split('T')[0];
        localStorage.removeItem(`mission_completed_${today}`);

        // Note: The success animation is already showing at this point
        // The fade to login will happen when handleAnimationComplete is called after Lottie finishes
    };

    const handleNext = async (newData: Partial<OnboardingData>) => {
        if (isTransitioning) return;

        // Special handling for Step 4 (Focus) - Sub-steps for BOTH track
        if (step === 4 && data.track === 'BOTH' && subStep === 'osp') {
            setData(prev => ({ ...prev, ...newData }));

            // Trigger internal transition
            setIsTransitioning(true);
            setContentOpacity('opacity-0');
            setTimeout(() => {
                setSubStep('zsv');
                setIsTransitioning(false);
                setContentOpacity('opacity-100');
            }, 300);
            return;
        }

        // Store the new data for later processing
        pendingDataRef.current = newData;

        if (step === 6 || (step === 5 && data.track !== 'BOTH')) {
            // Step 6 (BOTH track) OR Step 5 (OSP/ZSV/UNSURE tracks)
            // -> Success Animation -> Account Creation
            setShowSuccessAnimation(true);
        } else {
            // Phase 1: Fade out current content
            setIsTransitioning(true);
            setContentOpacity('opacity-0');

            // For other steps: Just wait for animation
            setTimeout(() => {
                handleAnimationComplete();
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, 400);
        }
    };

    const handleBack = () => {
        // Special handling for Step 4 Both track sub-steps
        if (step === 4 && data.track === 'BOTH' && subStep === 'zsv') {
            setIsTransitioning(true);
            setContentOpacity('opacity-0');
            setTimeout(() => {
                setSubStep('osp');
                setIsTransitioning(false);
                setContentOpacity('opacity-100');
            }, 300);
            return;
        }

        if (step > 1) {
            setStep(step - 1);
            // If going back to step 4 Both track, reset to OSP sub-step
            if (step - 1 === 4) setSubStep('osp');
        } else {
            navigate('/login');
        }
    };

    const handleClose = () => {
        navigate('/login');
    };

    // Determine which step component to render
    const renderStepContent = () => {
        if (showAccountCreation) {
            return <AccountCreation />;
        }

        switch (step) {
            case 1:
                return (
                    <OnboardingTrack
                        onNext={handleNext}
                        initialData={data}
                        isTransitioning={isTransitioning}
                    />
                );
            case 2:
                return (
                    <HonestAssessment
                        onNext={(assessmentData) => {
                            // Map userLevel to appropriate flags for plan generation
                            const { userLevel } = assessmentData;
                            const enhancedData: Partial<OnboardingData> = {
                                userLevel,
                            };

                            // Add level-specific configurations
                            if (userLevel === 'clean_slate') {
                                enhancedData.ospLevel = 'never';
                                enhancedData.zsvLevel = 'zero';
                            } else if (userLevel === 'plateau') {
                                enhancedData.ospLevel = 'weak';
                                enhancedData.zsvLevel = 'gaps';
                            } else if (userLevel === 'polisher') {
                                enhancedData.ospLevel = 'ok';
                                enhancedData.zsvLevel = 'ok';
                            }

                            handleNext(enhancedData);
                        }}
                        isTransitioning={isTransitioning}
                    />
                );
            case 3:
                // Skip focus if UNSURE track
                if (data.track === 'UNSURE') {
                    setTimeout(() => handleNext({}), 0);
                    return null;
                }
                return (
                    <OnboardingCadence
                        onNext={handleNext}
                        onBack={handleBack}
                        initialData={data}
                        isTransitioning={isTransitioning}
                    />
                );
            case 4:
                // Focus areas (with sub-step handling for BOTH)
                return (
                    <OnboardingFocus
                        onNext={handleNext}
                        onBack={handleBack}
                        initialData={data}
                        isTransitioning={isTransitioning}
                        subStep={subStep}
                    />
                );
            case 5:
                // For BOTH track with zsv substep, auto-advance to exam date (step 6)
                if (data.track === 'BOTH' && subStep === 'zsv') {
                    setTimeout(() => {
                        setStep(6);
                        setIsTransitioning(false);
                        setContentOpacity('opacity-100');
                    }, 0);
                    return null;
                }
                // For OSP/ZSV/UNSURE tracks, this is the exam date screen
                // For BOTH track with osp substep, this shouldn't happen (handled above)
                return (
                    <OnboardingExamDate
                        onNext={handleNext}
                        onBack={handleBack}
                        initialData={data}
                        isTransitioning={isTransitioning}
                    />
                );
            case 6:
                // This is ONLY for BOTH track - exam date screen
                // OSP/ZSV tracks should never reach here (celebration triggered from step 5)
                if (data.track === 'BOTH') {
                    return (
                        <OnboardingExamDate
                            onNext={handleNext}
                            onBack={handleBack}
                            initialData={data}
                            isTransitioning={isTransitioning}
                        />
                    );
                }
                return null;
            default:
                return null;
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const getStepTitle = () => {
        if (showAccountCreation) return 'Vytvoř si účet';
        if (step === 6) return 'Tvůj plán je připraven';
        return 'Vytvoř si svůj plán';
    };

    const showStepIndicator = !showAccountCreation && step < 7;
    const showBackButton = step > 1 && !showAccountCreation;
    const showCloseButton = !showAccountCreation;

    // Calculate progress for Duolingo-style segmented bar
    const getProgressSegments = () => {
        const totalSegments = 5;
        const currentStepNum = getCurrentStepDisplay();
        return {
            completed: Math.max(0, currentStepNum - 1),
            total: totalSegments
        };
    };

    const progress = getProgressSegments();

    return (
        <div className="fixed inset-0 z-50 bg-light-bg dark:bg-dark-bg overflow-y-auto">
            {/* Fullscreen background with centered content */}
            <div className="min-h-screen flex flex-col">
                {/* Minimal Header with Duolingo-style Progress Bar */}
                <header className="pt-4 pb-2 px-4">
                    <div className="max-w-2xl mx-auto">
                        <div className="flex items-center gap-3 mb-4">
                            {showBackButton && (
                                <button
                                    onClick={handleBack}
                                    className="p-2 -ml-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors flex-shrink-0"
                                >
                                    <ChevronLeft className="w-6 h-6 text-gray-500" />
                                </button>
                            )}

                            {/* Duolingo-style Segmented Progress Bar */}
                            {showStepIndicator && (
                                <div className="flex-1 flex gap-1.5 h-3">
                                    {Array.from({ length: progress.total }, (_, i) => {
                                        const isCompleted = i < progress.completed;
                                        const isCurrent = i === progress.completed;

                                        return (
                                            <div
                                                key={i}
                                                className={`flex-1 rounded-full transition-all duration-300 ${isCompleted
                                                    ? 'bg-brand-orange'
                                                    : isCurrent
                                                        ? 'bg-brand-orange animate-pulse'
                                                        : 'bg-gray-200 dark:bg-gray-700'
                                                    }`}
                                            />
                                        );
                                    })}
                                </div>
                            )}

                            {showCloseButton && (
                                <button
                                    onClick={handleClose}
                                    className="p-2 -mr-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors flex-shrink-0"
                                >
                                    <X className="w-6 h-6 text-gray-500" />
                                </button>
                            )}
                        </div>
                    </div>
                </header>

                {/* Main Content */}
                <main className="flex-1 flex flex-col items-center justify-center py-8 px-4 sm:px-6 lg:px-8">
                    <div className="w-full max-w-xl">
                        {/* Success Animation with Fade Transition */}
                        {showSuccessAnimation && (
                            <div className={`flex items-center justify-center h-64 transition-all duration-500 ${fadeToLogin ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
                                <DotLottieReact
                                    src="/materials/Success.lottie"
                                    loop={false}
                                    autoplay={true}
                                    dotLottieRefCallback={setDotLottie}
                                    className="w-48 h-48"
                                />
                            </div>
                        )}

                        {/* Step Content with Transition */}
                        {!showSuccessAnimation && (
                            <div className={`transition-opacity duration-300 ${contentOpacity}`}>
                                {renderStepContent()}
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default PreAuthWizard;
